/**
 * Sysadmin Platform — Administrators (Wave 1a / W1a-22).
 *
 * Lists every platform administrator (active + inactive), promotes new
 * sysadmins by user UUID, demotes existing ones (with self / inactive /
 * last-sysadmin guards enforced server-side), rotates TOTP secrets, and
 * edits the platform-wide password policy.
 *
 * Backed by the 6 endpoints under `/api/v1/sysadmin/sysadmins`.
 * Token is the sysadmin context JWT (`argusai-admin` audience) — set by
 * the W1a-15 login flow + persisted in localStorage.accessToken.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  ShieldCheck, MoreHorizontal, UserPlus, KeyRound, UserMinus, Copy,
  AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { StepUpReasonDialog } from '@/components/ui/step-up-reason-dialog';
import {
  useSysadmins, usePromoteSysadmin, useDemoteSysadmin,
  useRotateSysadminTotp, usePasswordPolicy, useUpdatePasswordPolicy,
  isLikelyUserId,
  type SystemAdminRow, type PasswordPolicy,
} from '@/lib/use-sysadmins';
import { useAuth } from '@/contexts/auth-context';

export const Route = createFileRoute('/sysadmin/admins')({
  component: PlatformAdminsPage,
});

// ─── Page ────────────────────────────────────────────────────────────────────

function PlatformAdminsPage() {
  const { data, isLoading, error } = useSysadmins();
  const { user } = useAuth();
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [demoteTarget, setDemoteTarget] = useState<SystemAdminRow | null>(null);
  const [rotateTarget, setRotateTarget] = useState<SystemAdminRow | null>(null);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" />
            Platform Administrators
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage sysadmins, rotate TOTP, and configure the platform password policy.
          </p>
        </div>
        <Button onClick={() => setPromoteOpen(true)} className="shrink-0">
          <UserPlus className="h-4 w-4 mr-2" />
          Promote user
        </Button>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatTile label="Active" value={data?.totalActive ?? '—'} loading={isLoading} />
        <StatTile label="Inactive" value={data?.totalInactive ?? '—'} loading={isLoading} />
        <StatTile
          label="Pending TOTP enrollment"
          value={
            data?.sysadmins
              ? data.sysadmins.filter((s) => s.isActive && !s.totpEnrolledAt).length
              : '—'
          }
          loading={isLoading}
        />
      </div>

      {/* Sysadmin table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Sysadmin roster</CardTitle>
          <CardDescription>
            Active rows can sign in with sysadmin context. Inactive rows are
            preserved for audit history.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          {error && (
            <div className="px-6 py-4 text-sm text-destructive">
              Failed to load sysadmins: {error.message}
            </div>
          )}
          {!error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>TOTP</TableHead>
                  <TableHead>Last login</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && data?.sysadmins.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No sysadmins found. (You shouldn't be seeing this — ask your install team.)
                    </TableCell>
                  </TableRow>
                )}
                {data?.sysadmins.map((row) => {
                  const isSelf = row.userId === user.id;
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">
                        {row.email}
                        {isSelf && (
                          <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}
                      </TableCell>
                      <TableCell>
                        {row.isActive ? (
                          <Badge variant="default">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.totpEnrolledAt ? (
                          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3 text-green-600" />
                            Enrolled
                          </span>
                        ) : (
                          <span className="text-xs text-amber-600 inline-flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Pending
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(row.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Row actions">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={!row.isActive}
                              onSelect={() => setRotateTarget(row)}
                            >
                              <KeyRound className="h-3 w-3 mr-2" />
                              Rotate TOTP
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={!row.isActive || isSelf}
                              onSelect={() => setDemoteTarget(row)}
                              className="text-destructive focus:text-destructive"
                            >
                              <UserMinus className="h-3 w-3 mr-2" />
                              Demote
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Password policy editor */}
      <PasswordPolicyCard />

      {/* Dialogs */}
      <PromoteDialog open={promoteOpen} onOpenChange={setPromoteOpen} />
      <DemoteDialog
        target={demoteTarget}
        onOpenChange={(open) => !open && setDemoteTarget(null)}
      />
      <RotateTotpDialog
        target={rotateTarget}
        onOpenChange={(open) => !open && setRotateTarget(null)}
      />
    </div>
  );
}

// ─── Stat tile ──────────────────────────────────────────────────────────────

function StatTile({ label, value, loading }: {
  label: string;
  value: number | string;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold mt-1">
          {loading ? '—' : value}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Promote dialog ─────────────────────────────────────────────────────────

function PromoteDialog({ open, onOpenChange }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [userId, setUserId] = useState('');
  // #1136 — Tier-1 destructive: collect the X-Step-Up-Reason inline.
  const [stepUpReason, setStepUpReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const promote = usePromoteSysadmin();

  const reset = () => {
    setUserId('');
    setStepUpReason('');
    setError(null);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const reasonOk =
    stepUpReason.trim().length >= 3 && stepUpReason.trim().length <= 500;

  const handleSubmit = async () => {
    setError(null);
    if (!isLikelyUserId(userId)) {
      setError('That does not look like a UUID. Find the user ID in the user-detail page URL.');
      return;
    }
    if (!reasonOk) {
      setError('Audit reason must be 3-500 characters.');
      return;
    }
    try {
      await promote.mutateAsync({
        userId: userId.trim(),
        stepUpReason: stepUpReason.trim(),
      });
      reset();
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Promote failed';
      setError(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Promote user to sysadmin</DialogTitle>
          <DialogDescription>
            Grants the platform-administrator role. The promoted user will be
            prompted to enroll TOTP at their next sysadmin login.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="user-id">User ID (UUID)</Label>
            <Input
              id="user-id"
              placeholder="00000000-0000-0000-0000-000000000000"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-xs text-muted-foreground">
              Cross-tenant user search lands in F-W1a-22.6. For now paste a
              UUID from the tenant-admin user-detail URL.
            </p>
          </div>

          {/* #1136 — X-Step-Up-Reason audit textarea. The backend's
              `require-fresh-totp.ts` Tier-1 gate refuses any
              destructive sysadmin mutation without this header. */}
          <div className="space-y-2">
            <Label htmlFor="promote-step-up-reason">
              Audit reason <span className="text-muted-foreground">(required)</span>
            </Label>
            <Textarea
              id="promote-step-up-reason"
              value={stepUpReason}
              onChange={(e) => setStepUpReason(e.target.value)}
              placeholder="Ticket / change request / incident reference (3-500 chars)"
              className="min-h-[72px] text-sm"
              maxLength={500}
            />
            <p className="text-[10px] text-muted-foreground">
              Recorded in the platform_audit_logs row.
            </p>
          </div>

          {error && (
            <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={promote.isPending || !userId.trim() || !reasonOk}
          >
            {promote.isPending ? 'Promoting…' : 'Promote'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Demote dialog ──────────────────────────────────────────────────────────

function DemoteDialog({ target, onOpenChange }: {
  target: SystemAdminRow | null;
  onOpenChange: (open: boolean) => void;
}) {
  const demote = useDemoteSysadmin();
  const [confirmText, setConfirmText] = useState('');
  // #1136 — Tier-1 destructive: collect the X-Step-Up-Reason inline.
  const [stepUpReason, setStepUpReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reasonOk =
    stepUpReason.trim().length >= 3 && stepUpReason.trim().length <= 500;

  const handleSubmit = async () => {
    if (!target) return;
    setError(null);
    if (!reasonOk) {
      setError('Audit reason must be 3-500 characters.');
      return;
    }
    try {
      await demote.mutateAsync({
        id: target.id,
        stepUpReason: stepUpReason.trim(),
      });
      setConfirmText('');
      setStepUpReason('');
      onOpenChange(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Demote failed';
      setError(msg);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Demote sysadmin</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{target?.email}</span> will
            lose platform-admin access. Their `system_admins` row is preserved
            for audit history (soft demote).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="bg-amber-500/10 border border-amber-500/20 rounded p-3 text-sm flex gap-2 items-start">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              Type <code className="bg-background px-1 rounded">demote</code> below to confirm.
              The action is reversible: re-promote them and the row is reactivated.
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-demote">Confirmation</Label>
            <Input
              id="confirm-demote"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="demote"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          {/* #1136 — X-Step-Up-Reason. Forwarded as header on the
              DELETE request via stepUpInit() in use-sysadmins.ts. */}
          <div className="space-y-2">
            <Label htmlFor="demote-step-up-reason">
              Audit reason <span className="text-muted-foreground">(required)</span>
            </Label>
            <Textarea
              id="demote-step-up-reason"
              value={stepUpReason}
              onChange={(e) => setStepUpReason(e.target.value)}
              placeholder="Ticket / change request / incident reference (3-500 chars)"
              className="min-h-[72px] text-sm"
              maxLength={500}
            />
          </div>

          {error && (
            <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={
              confirmText !== 'demote' || demote.isPending || !reasonOk
            }
            onClick={handleSubmit}
          >
            {demote.isPending ? 'Demoting…' : 'Demote'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Rotate TOTP dialog ─────────────────────────────────────────────────────

function RotateTotpDialog({ target, onOpenChange }: {
  target: SystemAdminRow | null;
  onOpenChange: (open: boolean) => void;
}) {
  const rotate = useRotateSysadminTotp();
  const [secret, setSecret] = useState<string | null>(null);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // R6 P0-1: the acting sysadmin's own 6-digit TOTP code. Required by
  // the API for step-up MFA — prevents a stolen access token from
  // rotating other sysadmins' secrets.
  const [totpCode, setTotpCode] = useState('');
  // #1136 — additional X-Step-Up-Reason header collected inline.
  const [stepUpReason, setStepUpReason] = useState('');

  const reasonOk =
    stepUpReason.trim().length >= 3 && stepUpReason.trim().length <= 500;

  const handleRotate = async () => {
    if (!target) return;
    setError(null);
    if (!reasonOk) {
      setError('Audit reason must be 3-500 characters.');
      return;
    }
    try {
      const res = await rotate.mutateAsync({
        id: target.id,
        totpCode,
        stepUpReason: stepUpReason.trim(),
      });
      setSecret(res.secret);
      setQrUri(res.qrUri);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Rotation failed';
      setError(msg);
    }
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setSecret(null);
      setQrUri(null);
      setError(null);
      setTotpCode('');
      setStepUpReason('');
    }
    onOpenChange(next);
  };

  const codeValid = /^\d{6}$/.test(totpCode.trim());

  return (
    <Dialog open={!!target} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rotate TOTP secret</DialogTitle>
          <DialogDescription>
            For <span className="font-medium text-foreground">{target?.email}</span>.
            The new secret is shown only once. Save it now or the user must
            request another rotation.
          </DialogDescription>
        </DialogHeader>

        {!secret && (
          <div className="space-y-3">
            <div className="bg-amber-500/10 border border-amber-500/20 rounded p-3 text-sm flex gap-2 items-start">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                Rotating revokes the old TOTP. The user will be locked out
                until they enroll the new secret.
              </div>
            </div>
            {/* R6 P0-1: step-up MFA — collect the ACTING sysadmin's
                fresh code. We label this clearly as YOUR code (not
                the target's) so the operator does not mis-enter. */}
            <div className="space-y-1">
              <Label htmlFor="acting-totp">
                Your authenticator code (6 digits)
              </Label>
              <Input
                id="acting-totp"
                value={totpCode}
                onChange={(e) =>
                  setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                placeholder="123456"
                className="font-mono tracking-widest text-center"
                aria-describedby="acting-totp-help"
              />
              <p
                id="acting-totp-help"
                className="text-xs text-muted-foreground"
              >
                Open your own authenticator app and enter the code shown
                for ArgusAI. This proves you have a fresh second factor
                — it does NOT change your own secret.
              </p>
            </div>
            {/* #1136 — X-Step-Up-Reason audit textarea. The backend
                Tier-1 gate refuses TOTP rotation without this header. */}
            <div className="space-y-1">
              <Label htmlFor="rotate-step-up-reason">
                Audit reason <span className="text-muted-foreground">(required)</span>
              </Label>
              <Textarea
                id="rotate-step-up-reason"
                value={stepUpReason}
                onChange={(e) => setStepUpReason(e.target.value)}
                placeholder="Ticket / change request / incident reference (3-500 chars)"
                className="min-h-[72px] text-sm"
                maxLength={500}
              />
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded">
                {error}
              </div>
            )}
            <Button
              onClick={handleRotate}
              disabled={rotate.isPending || !codeValid || !reasonOk}
              className="w-full"
            >
              {rotate.isPending ? 'Rotating…' : 'Generate new secret'}
            </Button>
          </div>
        )}

        {secret && qrUri && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Authenticator URI</Label>
              <div className="flex gap-2">
                <Input value={qrUri} readOnly className="font-mono text-xs" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigator.clipboard.writeText(qrUri)}
                  title="Copy URI"
                  aria-label="Copy authenticator URI"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Manual-entry secret (base32)</Label>
              <div className="flex gap-2">
                <Input value={secret} readOnly className="font-mono text-xs" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigator.clipboard.writeText(secret)}
                  title="Copy secret"
                  aria-label="Copy manual-entry secret"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Paste the URI into a QR generator or scan with Google
              Authenticator / 1Password / Authy. Once you close this dialog,
              the secret cannot be retrieved.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant={secret ? 'default' : 'outline'} onClick={() => handleClose(false)}>
            {secret ? 'Done' : 'Cancel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Password policy card ───────────────────────────────────────────────────

function PasswordPolicyCard() {
  const { data, isLoading } = usePasswordPolicy();
  const update = useUpdatePasswordPolicy();
  const [draft, setDraft] = useState<Partial<PasswordPolicy>>({});
  const [savedTimer, setSavedTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  // #1136 — Tier-1/Tier-2 destructive: open the reason dialog before
  // submitting. The backend's require-fresh-totp.ts refuses the PATCH
  // without `X-Step-Up-Reason`.
  const [reasonOpen, setReasonOpen] = useState(false);

  const policy: PasswordPolicy = {
    ...(data?.policy ?? {
      minLength: 12,
      requireUppercase: true,
      requireLowercase: true,
      requireNumber: true,
      requireSymbol: false,
      maxAgeDays: 0,
    }),
    ...draft,
  };

  const dirty = Object.keys(draft).length > 0;

  const handleSave = () => {
    if (!dirty) return;
    setReasonOpen(true);
  };

  // #1136 — actually submit the PATCH after the operator provides the
  // X-Step-Up-Reason text. Threading through the new hook variables
  // shape `{ patch, stepUpReason }`.
  const doSave = async (stepUpReason: string) => {
    if (!dirty) return;
    try {
      await update.mutateAsync({ patch: draft, stepUpReason });
      setDraft({});
      setShowSaved(true);
      if (savedTimer) clearTimeout(savedTimer);
      setSavedTimer(setTimeout(() => setShowSaved(false), 2500));
      setReasonOpen(false);
    } catch {
      // Close the reason dialog so the server error surfaces on the
      // card.
      setReasonOpen(false);
    }
  };

  const handleReset = () => {
    setDraft({});
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Password policy</CardTitle>
        <CardDescription>
          Platform-wide password requirements. Changes apply to new passwords
          and password resets; existing passwords are validated against the
          policy on next login.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="min-length">Minimum length</Label>
            <Input
              id="min-length"
              type="number"
              min={8}
              max={128}
              value={policy.minLength}
              onChange={(e) =>
                setDraft({ ...draft, minLength: Number(e.target.value) })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="max-age">Rotation period (days, 0 = never)</Label>
            <Input
              id="max-age"
              type="number"
              min={0}
              max={365}
              value={policy.maxAgeDays}
              onChange={(e) =>
                setDraft({ ...draft, maxAgeDays: Number(e.target.value) })
              }
            />
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PolicySwitch
            label="Require uppercase letter"
            checked={policy.requireUppercase}
            onChange={(v) => setDraft({ ...draft, requireUppercase: v })}
          />
          <PolicySwitch
            label="Require lowercase letter"
            checked={policy.requireLowercase}
            onChange={(v) => setDraft({ ...draft, requireLowercase: v })}
          />
          <PolicySwitch
            label="Require number"
            checked={policy.requireNumber}
            onChange={(v) => setDraft({ ...draft, requireNumber: v })}
          />
          <PolicySwitch
            label="Require symbol"
            checked={policy.requireSymbol}
            onChange={(v) => setDraft({ ...draft, requireSymbol: v })}
          />
        </div>

        <Separator />

        <div className="flex items-center justify-end gap-2">
          {showSaved && (
            <span className="text-xs text-green-600 inline-flex items-center gap-1 mr-auto">
              <CheckCircle2 className="h-3 w-3" />
              Saved
            </span>
          )}
          {dirty && (
            <Button variant="outline" onClick={handleReset} disabled={update.isPending}>
              Reset
            </Button>
          )}
          <Button onClick={handleSave} disabled={!dirty || update.isPending || isLoading}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>

      {/* #1136 — X-Step-Up-Reason audit prompt before the PATCH fires. */}
      {reasonOpen && (
        <StepUpReasonDialog
          open
          onOpenChange={(o) => !o && setReasonOpen(false)}
          title="Audit reason for password-policy change"
          description="Tier-1 step-up. Changes apply platform-wide to new passwords and password resets. The reason is recorded in the platform_audit_logs row."
          submitLabel={update.isPending ? 'Saving…' : 'Save policy'}
          submitting={update.isPending}
          onConfirm={(reason) => doSave(reason)}
        />
      )}
    </Card>
  );
}

function PolicySwitch({ label, checked, onChange }: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 px-3 py-2 rounded border cursor-pointer hover:bg-accent">
      <span className="text-sm">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}
